import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import type { DocumentSlot } from '@core/util/documentSlots.util';
import {
  buildUploadMetadata,
  validateRequiredDocumentDates,
} from '@core/util/assetMetadata.util';
import type { AssetUploadParams } from '@/hooks/useAsset';

interface UploadDocumentSlotProps {
  slot: DocumentSlot;
  onUpload: (params: AssetUploadParams) => Promise<boolean>;
}

const UploadDocumentSlot: React.FC<UploadDocumentSlotProps> = ({
  slot,
  onUpload,
}) => {
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const hasIssueDate = Boolean(slot.dateRequirements.issueDate);
  const hasExpiryDate = Boolean(slot.dateRequirements.expiryDate);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const validationError = validateRequiredDocumentDates(
      { issueDate, expiryDate },
      slot.dateRequirements,
    );
    if (validationError) {
      toast.error(validationError, {
        position: 'bottom-left',
        autoClose: 4000,
      });
      return;
    }

    setIsUploading(true);
    try {
      const metadata = buildUploadMetadata({ issueDate, expiryDate });
      const success = await onUpload({
        tag: slot.tag,
        file,
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
      });
      if (success) {
        setIssueDate('');
        setExpiryDate('');
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      key={slot.tag}
      className="border-b border-gray-100 pb-4 last:border-b-0"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-900">
            {slot.title}
            {slot.required && (
              <span className="ml-1 text-red-500" aria-hidden="true">
                *
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            File not uploaded yet. PDF, JPG, JPEG, PNG, HEIF, and HEIC are
            supported.
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:min-w-[220px]">
          {hasIssueDate && (
            <label className="flex flex-col gap-1 text-xs text-gray-600">
              <span className="inline-flex items-center gap-0.5">
                {slot.dateRequirements.issueDate?.title ?? 'Issue date'}
                {slot.dateRequirements.issueDate?.required && (
                  <span className="text-red-500" aria-hidden="true">
                    *
                  </span>
                )}
              </span>
              <input
                type="date"
                value={issueDate}
                max={new Date().toISOString().split('T')[0]}
                onChange={(event) => setIssueDate(event.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900"
                disabled={isUploading}
              />
            </label>
          )}
          {hasExpiryDate && (
            <label className="flex flex-col gap-1 text-xs text-gray-600">
              <span className="inline-flex items-center gap-0.5">
                {slot.dateRequirements.expiryDate?.title ?? 'Expiry date'}
                {slot.dateRequirements.expiryDate?.required && (
                  <span className="text-red-500" aria-hidden="true">
                    *
                  </span>
                )}
              </span>
              <input
                type="date"
                value={expiryDate}
                min={new Date().toISOString().split('T')[0]}
                onChange={(event) => setExpiryDate(event.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900"
                disabled={isUploading}
              />
            </label>
          )}

          <label
            className={`flex cursor-pointer items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 ${isUploading ? 'pointer-events-none opacity-60' : ''}`}
            title="Upload a document"
          >
            <FontAwesomeIcon icon={faUpload} className="h-4 w-4" />
            {isUploading ? 'Uploading…' : 'Upload'}
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.heif,.heic"
              className="hidden"
              disabled={isUploading}
              onChange={(e) => void handleFileChange(e)}
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default UploadDocumentSlot;
